import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useBoltConfiguration,
  useFormValidation,
  useParameterAddition,
} from '../../../shared/lib';
import { useUIStore } from '../../../shared/stores/useUIStore';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import { useActiveProjectKey } from '../../../shared/hooks/ActiveDocumentProvider';
import { useBoltConfigurationApply } from '../../../shared/lib/hooks/useBoltConfigurationApply';
import { describeUndoPlanAffordance } from '../../../shared/lib/parameterUndo/undoAffordance';
import {
  filterParameterOptionsByDataType,
  filterTextParameterOptions,
} from '../../../shared/lib/revit/parameterOptionTypeFilter';
import type {
  ParameterOption,
  SharedParameterDataType,
} from '../../../shared/types/sharedParameter.types';
import {
  BOLT_CONNECTION_FIELDS,
  BOLT_CONNECTION_CONDITIONAL_FIELDS,
  withCondition,
} from '../../../shared/config/validationConfig';
import WorkflowFooter from '../../../shared/ui/workflow/WorkflowFooter';
import { OperationProgressWindowRequest } from '../../../shared/ui/progress';
import { useBoltFamilyAvailability } from '../lib/useBoltFamilyAvailability';
import { useParameterDropdownOptions } from '../lib/useParameterDropdownOptions';
import {
  BOLT_MODELING_OPTIONS,
  CORE_FIELD_DEFINITIONS,
  SUPPORTING_FIELD_DEFINITIONS,
  resultToNotification,
  type BoltFieldKey,
  type Notification,
} from '../lib/boltConnectionModel';
import {
  type SectionSummaryItem,
} from '../../../shared/ui/workflow/WorkflowStepComponents';
import { formatParameterSetupTargetLabel } from '../../../shared/ui/workflow/ParameterSetupScopePanel';
import { boltConnectionTooltips } from '../../../shared/config/wizardTooltips';
import { getContinueToNextWorkflowStepText } from '../../../shared/config/workflowUi';
import { BoltConnectionPageView } from './BoltConnectionPageView';

interface BoltConnectionPageProps {
  onGoBack: () => void;
  onContinue: () => void;
}

const BoltConnectionPage: React.FC<BoltConnectionPageProps> = ({
  onGoBack,
  onContinue,
}) => {
  const {
    config,
    updateField,
    setParameterTarget,
    setProjectParameterCategories,
  } = useBoltConfiguration();
  const objectTypeParameterName = useConfigurationStore(
    (state) => state.menuConfiguration.objectTypeValue
  );
  const objectTypeBindingKind = useConfigurationStore(
    (state) => state.connectorBindingConfiguration.parameterBindingKind
  );
  const objectTypeDestination = useConfigurationStore(
    (state) => state.connectorBindingConfiguration.parameterDestination
  );
  const defaultPropertiesConfirmed = useUIStore((state) => state.defaultPropertiesConfirmed);
  const setDefaultPropertiesConfirmed = useUIStore((state) => state.setDefaultPropertiesConfirmed);
  const activeProjectKey = useActiveProjectKey();
  const activeSection = useUIStore((state) => state.sections.boltConnection);
  const setActiveSection = useUIStore((state) => state.setBoltConnectionSection);
  const selectedOption = useUIStore((state) => state.selectedOption);
  const issueNavigationContext = usePcfValidationStore((state) => state.issueNavigationContext);
  const { options: parameterOptions } = useParameterDropdownOptions(activeProjectKey);
  // Each bolt field binds a parameter of a fixed data type (BOLT_FIELD_DATA_TYPES);
  // offering a Text parameter for the Length diameter would produce values Revit
  // rejects downstream. Unknown-typed options stay visible (parameterOptionTypeFilter).
  const { textParameterOptions, parameterOptionsByDataType, yesNoParameterOptions } = useMemo(() => {
    const textOptions = filterTextParameterOptions(parameterOptions);
    return {
      textParameterOptions: textOptions,
      parameterOptionsByDataType: {
        LENGTH: filterParameterOptionsByDataType(parameterOptions, ['LENGTH']),
        INTEGER: filterParameterOptionsByDataType(parameterOptions, ['INTEGER']),
        TEXT: textOptions,
      } as Partial<Record<SharedParameterDataType, ParameterOption[]>>,
      yesNoParameterOptions: filterParameterOptionsByDataType(parameterOptions, ['YESNO']),
    };
  }, [parameterOptions]);
  const {
    isProcessing,
    progressModel,
    apply,
    syncToMapping,
    undoLastBoltParameterAddition,
    lastBoltUndoPlan,
    targetObjectType,
    boltBaseFamilyValue,
  } = useBoltConfigurationApply();
  const {
    isAdding: isFinalizingConnectorParameters,
    parameterProgress: connectorFinalizationProgress,
    addParameters,
  } = useParameterAddition();
  const [notification, setNotification] = useState<Notification>({
    open: false,
    severity: 'info',
    title: '',
    message: '',
  });
  const boltModelingChoice = config.boltModelingChoice.value === 'yes' ? 'yes' : 'no';
  const isGasketMode = boltModelingChoice === 'no';
  const modelingOption = BOLT_MODELING_OPTIONS.find((option) => option.value === boltModelingChoice) ?? BOLT_MODELING_OPTIONS[0];
  const {
    isCheckingAvailability,
    availabilityProgress,
    autoDefaultPropertiesConfirmedForActiveProject,
  } = useBoltFamilyAvailability({
    activeProjectKey,
    config,
    objectTypeParameterName,
    objectTypeDestination,
    objectTypeBindingKind,
    targetObjectType,
  });
  const setupConfirmed = defaultPropertiesConfirmed || autoDefaultPropertiesConfirmedForActiveProject;
  const selectedTargetLabel = formatParameterSetupTargetLabel(
    config.parameterDestination,
    config.parameterBindingKind
  );
  const boltTargetApplication =
    config.parameterDestination === 'family'
      ? `${selectedTargetLabel} applies to editable ${targetObjectType} families.`
      : `${selectedTargetLabel} applies to the selected Revit categories. PipeEX loads the ${targetObjectType} reference family only when no matching family exists.`;
  const boltUndoAffordance = describeUndoPlanAffordance(lastBoltUndoPlan);
  const hasUndoableBoltAddition =
    Boolean(activeProjectKey) &&
    lastBoltUndoPlan?.projectKey === activeProjectKey &&
    boltUndoAffordance.visible;
  const continueToNextStepText = useMemo(
    () => getContinueToNextWorkflowStepText('boltConnection', selectedOption),
    [selectedOption]
  );

  const validationFields = useMemo(
    () => [
      ...BOLT_CONNECTION_FIELDS,
      ...withCondition(BOLT_CONNECTION_CONDITIONAL_FIELDS, () => boltModelingChoice === 'no'),
    ],
    [boltModelingChoice]
  );

  const {
    validation,
    getFieldError,
    markFieldTouched,
    showValidationErrors,
    setShowValidationErrors,
  } = useFormValidation({
    config,
    fields: validationFields,
  });

  const validationFocusFields = useMemo(
    () =>
      new Set(
        (issueNavigationContext?.actionParams?.boltFields || issueNavigationContext?.affectedItems || [])
          .map((item) => item.trim().toUpperCase().replace(/[\s_]+/g, '-'))
          .filter(Boolean)
      ),
    [issueNavigationContext?.actionParams?.boltFields, issueNavigationContext?.affectedItems]
  );

  const isValidationFocused = useCallback(
    (...tokens: string[]) =>
      tokens.some((token) => validationFocusFields.has(token.trim().toUpperCase().replace(/[\s_]+/g, '-'))),
    [validationFocusFields]
  );

  useEffect(() => {
    updateField('boltBaseFamilyValue', boltBaseFamilyValue);
  }, [boltBaseFamilyValue, updateField]);

  const createFieldHandler = useCallback(
    (fieldName: Exclude<BoltFieldKey, 'boltBaseFamilyValue' | 'boltModelingChoice'>) => (value: string) => {
      updateField(fieldName, value);
      markFieldTouched(fieldName);
    },
    [markFieldTouched, updateField]
  );

  const fieldHandlers = useMemo(
    () => ({
      boltDiaValue: createFieldHandler('boltDiaValue'),
      boltQuantityValue: createFieldHandler('boltQuantityValue'),
      boltLengthValue: createFieldHandler('boltLengthValue'),
      boltItemDescriptionValue: createFieldHandler('boltItemDescriptionValue'),
      boltMaterialValue: createFieldHandler('boltMaterialValue'),
      boltStandardValue: createFieldHandler('boltStandardValue'),
      boltSetActiveValue: createFieldHandler('boltSetActiveValue'),
    }),
    [createFieldHandler]
  );

  const activeCoreFields = useMemo(
    () => (isGasketMode ? CORE_FIELD_DEFINITIONS : CORE_FIELD_DEFINITIONS.slice(0, 3)),
    [isGasketMode]
  );

  const activeSupportingFields = useMemo(
    () => SUPPORTING_FIELD_DEFINITIONS.filter((field) =>
      field.field === 'boltBaseFamilyValue' || isGasketMode
    ),
    [isGasketMode]
  );

  const requiredFieldDefinitions = useMemo(
    () => [...activeCoreFields, ...activeSupportingFields.filter((field) => field.required)],
    [activeCoreFields, activeSupportingFields]
  );

  const requiredMappingCount = requiredFieldDefinitions.length;
  const requiredMappingsReady = requiredFieldDefinitions.filter((field) => {
    const value = config[field.field].value;
    return value && value.trim() !== '';
  }).length;

  const firstValidationError = useMemo(() => {
    const orderedFields = [...activeCoreFields, ...activeSupportingFields];
    for (const field of orderedFields) {
      const message = validation.errors[field.field];
      if (message) return message;
    }
    return null;
  }, [activeCoreFields, activeSupportingFields, validation.errors]);

  const blockingReasons = useMemo(() => {
    const reasons: string[] = [];
    if (!setupConfirmed) reasons.push('confirm creation of missing Revit parameters');
    if (!validation.isValid) {
      if (Object.keys(validation.errors).length === 1 && firstValidationError) {
        reasons.push(firstValidationError);
      } else if (validation.missingFields.length > 0) {
        reasons.push(`${validation.missingFields.length} required bolt mapping${validation.missingFields.length === 1 ? '' : 's'} missing`);
      } else if (firstValidationError) {
        reasons.push(firstValidationError);
      }
    }
    return reasons;
  }, [firstValidationError, setupConfirmed, validation.errors, validation.isValid, validation.missingFields.length]);

  const footerContent = useMemo(() => {
    return (
      <WorkflowFooter
        isReady={blockingReasons.length === 0}
        readyText={`${requiredMappingsReady}/${requiredMappingCount} mapped`}
        blockedReasons={blockingReasons}
      />
    );
  }, [blockingReasons, requiredMappingsReady, requiredMappingCount]);

  const handleContinue = useCallback(async () => {
    if (!validation.isValid) {
      setShowValidationErrors(true);
      return;
    }
    if (!setupConfirmed) return;
    const liveConnectorConfig = useConfigurationStore.getState().connectorBindingConfiguration;
    const connectorTarget = {
      destination: liveConnectorConfig.parameterDestination,
      bindingKind: liveConnectorConfig.parameterBindingKind,
    };

    const result = await apply();
    setNotification(resultToNotification(result));
    if (!result.success) {
      return;
    }

    syncToMapping();
    if (selectedOption !== 'Iso Drawings Generation') {
      const connectorFinalizationResult = await addParameters({ connectorTarget });
      if (!connectorFinalizationResult.success) {
        setNotification({
          open: true,
          severity: 'error',
           title: 'Connector parameters could not be finalized',
          message:
            connectorFinalizationResult.error ||
            'Connector parameter finalization did not complete for all selected Revit families.',
        });
        return;
      }
    }
    onContinue();
  }, [
    addParameters,
    apply,
    onContinue,
    selectedOption,
    setShowValidationErrors,
    setupConfirmed,
    syncToMapping,
    validation.isValid,
  ]);

  const handleNotificationClose = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const handleTargetChange = useCallback(
    (target: { destination: 'project' | 'family'; bindingKind: 'type' | 'instance' }) => {
      setParameterTarget(target);
    },
    [setParameterTarget]
  );

  const handleBoltProjectCategoryChange = useCallback(
    (category: string, checked: boolean) => {
      const currentCategories = Array.isArray(config.projectParameterCategories)
        ? config.projectParameterCategories
        : ['Pipe Accessories'];
      const nextCategories = checked
        ? Array.from(new Set([...currentCategories, category]))
        : currentCategories.filter((currentCategory) => currentCategory !== category);

      setProjectParameterCategories(nextCategories);
    },
    [config.projectParameterCategories, setProjectParameterCategories]
  );

  const handleUndoLastAddition = useCallback(async () => {
    const undone = await undoLastBoltParameterAddition();
    if (undone) {
      setNotification({
        open: true,
        severity: 'info',
         title: 'Bolt parameter changes reverted',
         message: 'The latest bolt parameter changes were removed from this Revit project.',
      });
    }
  }, [undoLastBoltParameterAddition]);

  const handleBoltModelingChoiceChange = useCallback(
    (value: string) => {
      updateField('boltModelingChoice', value);
      setShowValidationErrors(false);
    },
    [setShowValidationErrors, updateField]
  );

  const canProceed =
    setupConfirmed &&
    validation.isValid &&
    !isProcessing &&
    !isFinalizingConnectorParameters;
  const activeSectionIndex = activeSection === 'supporting' ? 1 : 0;

  const sectionStates = useMemo(() => [
    {
      key: 'core',
      title: 'Core mappings',
      shortTitle: 'Core',
       description: 'Map the parameters required by the selected PCF strategy.',
      isComplete: activeCoreFields.every(f => !f.required || (config[f.field].value || '').trim()),
      hasError: activeCoreFields.some(f => validation.errors[f.field]),
      configuredCount: activeCoreFields.filter(f => (config[f.field].value || '').trim()).length,
      totalCount: activeCoreFields.length,
    },
    {
      key: 'supporting',
      title: 'Supporting settings',
      shortTitle: 'Supporting',
       description: 'Review the derived PCF object family and gasket-path activation field.',
      isComplete: activeSupportingFields.every(f => !f.required || (config[f.field].value || '').trim()),
      hasError: activeSupportingFields.some(f => validation.errors[f.field]),
      configuredCount: activeSupportingFields.filter(f => (config[f.field].value || '').trim()).length,
      totalCount: activeSupportingFields.length,
    },
  ], [activeCoreFields, activeSupportingFields, config, validation.errors]);

  const sectionSummaryItems = useMemo<SectionSummaryItem[]>(
    () =>
      sectionStates.map((section, index) => {
        const sectionKey = index === 0 ? ('core' as const) : ('supporting' as const);
        const statusTone: SectionSummaryItem['statusTone'] = section.isComplete
          ? 'ready'
          : section.hasError
            ? 'missing'
            : 'optional';

        return {
        key: section.key,
        title: section.shortTitle,
        detail: section.description,
        help:
          sectionKey === 'core'
            ? boltConnectionTooltips.coreSection
            : boltConnectionTooltips.supportingSection,
        helpLabel: `${section.shortTitle} bolt section`,
        statusLabel: section.isComplete
          ? 'Ready'
          : section.hasError
            ? 'Needs attention'
            : `${section.configuredCount}/${section.totalCount} set`,
        statusTone,
        selected: activeSection === sectionKey,
        onClick: () => setActiveSection(sectionKey),
        role: 'tab',
        ariaSelected: activeSection === sectionKey,
        ariaControls: `bolt-section-panel-${section.key}`,
        id: `bolt-section-tab-${section.key}`,
        tabIndex: activeSection === sectionKey ? 0 : -1,
      };
    }),
    [activeSection, sectionStates, setActiveSection]
  );

  const activeSectionState = sectionStates[activeSectionIndex] ?? sectionStates[0];
  const activeFields = activeSection === 'core' ? activeCoreFields : activeSupportingFields;
  const activeSectionStatusLabel = activeSectionState.isComplete
    ? 'Ready'
    : activeSectionState.hasError
      ? 'Needs attention'
      : `${activeSectionState.configuredCount}/${activeSectionState.totalCount} set`;
  const activeSectionStatusTone = activeSectionState.isComplete
    ? 'ready'
    : activeSectionState.hasError
      ? 'missing'
      : 'optional';

  return (
    <>
      <OperationProgressWindowRequest
        id="workflow-bolt-parameter-setup"
        open={isProcessing}
        priority={100}
        model={progressModel ?? {
          title: 'Creating bolt parameters',
          message: 'Revit is creating the missing bolt parameters.',
          detail: boltTargetApplication,
          status: 'running',
          percent: null,
          locked: true,
        }}
      />
      <OperationProgressWindowRequest
        id="workflow-bolt-connector-finalization"
        open={isFinalizingConnectorParameters}
        priority={100}
        model={connectorFinalizationProgress ?? {
          title: 'Finalizing connector parameters',
          message: 'Revit is creating connector parameters for newly loaded bolt families.',
          detail: 'Only families introduced during bolt setup are checked.',
          status: 'running',
          percent: null,
          locked: true,
        }}
      />
      <BoltConnectionPageView
        isProcessing={isProcessing}
        progressModel={progressModel}
        isFinalizingConnectorParameters={isFinalizingConnectorParameters}
        connectorFinalizationProgress={connectorFinalizationProgress}
        boltTargetApplication={boltTargetApplication}
        onGoBack={onGoBack}
        onContinue={handleContinue}
        nextText={isProcessing || isFinalizingConnectorParameters
          ? 'Adding…'
          : hasUndoableBoltAddition ? continueToNextStepText : 'Continue'}
        canProceed={canProceed}
        footerContent={footerContent}
        isCheckingAvailability={isCheckingAvailability}
        availabilityProgress={availabilityProgress}
        showValidationErrors={showValidationErrors}
        validationIsValid={validation.isValid}
        missingFields={validation.missingFields}
        setupConfirmed={setupConfirmed}
        autoConfirmed={autoDefaultPropertiesConfirmedForActiveProject}
        config={config}
        onTargetChange={handleTargetChange}
        onConfirmedChange={setDefaultPropertiesConfirmed}
        isGasketMode={isGasketMode}
        modelingOptionLabel={modelingOption.label}
        onToggleModelingChoice={() => handleBoltModelingChoiceChange(isGasketMode ? 'yes' : 'no')}
        hasUndoableBoltAddition={hasUndoableBoltAddition}
        boltUndoAffordance={boltUndoAffordance}
        onUndoLastAddition={() => void handleUndoLastAddition()}
        onProjectCategoryChange={handleBoltProjectCategoryChange}
        sectionSummaryItems={sectionSummaryItems}
        activeSectionState={activeSectionState}
        activeSectionStatusLabel={activeSectionStatusLabel}
        activeSectionStatusTone={activeSectionStatusTone}
        activeFields={activeFields}
        boltBaseFamilyValue={boltBaseFamilyValue}
        yesNoParameterOptions={yesNoParameterOptions}
        parameterOptionsByDataType={parameterOptionsByDataType}
        textParameterOptions={textParameterOptions}
        fieldHandlers={fieldHandlers}
        updateField={updateField}
        getFieldError={getFieldError}
        isValidationFocused={isValidationFocused}
        hasSharedParameterOptions={parameterOptions.length > 0}
        notification={notification}
        onNotificationClose={handleNotificationClose}
      />
    </>
  );
};

export default BoltConnectionPage;
